import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import GeometryLayer, { GeometryMode } from "plugins/shared/layers/GeometryLayer.class"
import { useMemo } from "react"
import { geometryDataStore, GeometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import { useNonReactiveCallback } from "@nova/use-non-reactive-callback"
import {
    bumpResetKey,
    cancelMaskDrawing,
    setDrawingType,
    UNSAVED_ORTHO_MASK_ID,
    selectOrthoMaskEditingFlightId,
    selectOrthoMaskEditingMaskId,
    selectOrthoUnsavedMask,
    selectOrthoMaskDrawingType,
    selectOrthoMaskResetKey,
} from "../OrthoMaskSlice"
import { createOrthoMask, updateOrthoMask } from "features/pubsub/entityActions/cubes/orthoMask"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import { selectUserID } from "user/profile/selectors"

const MASK_COLOR: [number, number, number, number] = [39, 187, 255, 64]
const MASK_LINE_COLOR: [number, number, number, number] = [39, 187, 255, 255]

export const useOrthoMaskLayer = () => {
    const dispatch = useAppDispatch()
    const editingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)
    const editingMaskId = useAppSelector(selectOrthoMaskEditingMaskId)
    const unsavedMask = useAppSelector(selectOrthoUnsavedMask)
    const drawingType = useAppSelector(selectOrthoMaskDrawingType)
    const _resetKey = useAppSelector(selectOrthoMaskResetKey)
    const userId = useAppSelector(selectUserID) || ""
    const existingMask = useAppSelector(selectOrthoMaskByFlightId(editingFlightId ?? ""))

    const data = useMemo(
        () => (unsavedMask ? [{ ...unsavedMask, deckGlIdx: 0 }] : []),
        [unsavedMask]
    )

    const onPolyComplete = useNonReactiveCallback(() => {
        dispatch(setDrawingType("edit"))
    }, [dispatch])

    const onReset = useNonReactiveCallback(() => {
        if (drawingType === "polygon") {
            geometryDataStore.setAll([])
        } else {
            geometryDataStore.setAll(existingMask?.polygon?.coordinates ?? [])
            dispatch(bumpResetKey())
        }
    }, [drawingType, existingMask, dispatch])

    const flushData = useNonReactiveCallback(
        async (store: GeometryDataStore, save: boolean) => {
            if (save && store.polygon.coordinates.length >= 3) {
                const polygon = { coordinates: store.polygon.coordinates }
                if (editingMaskId && editingMaskId !== UNSAVED_ORTHO_MASK_ID) {
                    dispatch(
                        updateOrthoMask({
                            id: editingMaskId,
                            updates: { polygon },
                        })
                    )
                } else if (editingFlightId) {
                    dispatch(
                        createOrthoMask({
                            data: {
                                flightId: editingFlightId,
                                polygon,
                                enabled: true,
                                createdBy: { userId },
                            },
                        })
                    )
                }
            }
            dispatch(cancelMaskDrawing())
        },
        [editingFlightId, editingMaskId, existingMask, userId]
    )

    if (!editingFlightId || data.length === 0) return []

    return new GeometryLayer({
        id: "ortho-mask-drawing",
        data,
        visible: true,
        pickable: false,
        editData: { canEdit: true, flush: flushData, onPolyComplete, onReset },
        drawData: { drawMode: "points", canDraw: drawingType === "polygon", maxPoints: 256 },
        mode: GeometryMode.LineToPolygon,
        extraLayers: [],
        geometryName: "Mask",
        styles: {
            points: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getFillColor: [255, 255, 255],
                lineWidthUnits: "pixels",
                getLineColor: MASK_LINE_COLOR,
            },
            selectedPoint: {
                filled: true,
                stroked: true,
                radiusMinPixels: 6,
                radiusMaxPixels: 6,
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: [255, 255, 255],
                getFillColor: MASK_LINE_COLOR,
            },
            midpoints: {
                filled: true,
                stroked: true,
                radiusMinPixels: 4,
                radiusMaxPixels: 4,
                getFillColor: [255, 255, 255, 255],
                getLineWidth: 2,
                lineWidthUnits: "pixels",
                getLineColor: MASK_LINE_COLOR,
            },
            lines: {
                widthScale: 1,
                widthMaxPixels: 8,
                widthMinPixels: 2,
                capRounded: false,
                billboard: false,
                getColor: () => MASK_LINE_COLOR,
                getWidth: 2,
                miterLimit: 0,
            },
            polygons: {
                stroked: true,
                getLineWidth: 3,
                lineWidthUnits: "pixels",
                lineMiterLimit: 0,
                getLineColor: MASK_LINE_COLOR,
                getFillColor: MASK_COLOR,
            },
        },
    })
}
